import React, { createRef, useState } from 'react'

import Moment from 'react-moment'
import { Link, useHistory } from 'react-router-dom'
import styled from 'styled-components'

import iconMapMarker from '../../../../assets/images/icon_map_marker.svg'
import iconVerticalDotMenu from '../../../../assets/images/icon_vertical_dot_menu.svg'
import { integrationBranding } from '../../../../config/integrationBranding'
import {
  integrationMenuGreyIcon,
  integrationMenuWhiteIcon,
} from '../../../../config/integrationMenuIcons'
import { useAdminZoneContext } from '../../../../contexts/adminZoneContext'
import { useUserContext } from '../../../../contexts/userContext'
import { useOnClickOutside } from '../../../../hooks/useOnClickOutside'
import { media } from '../../../../styles/breakpoints'
import { capitalizeString } from '../../../../utils'
import { Icon } from '../../../common/Icon'
import { checkUserPermissions } from '../../../common/PermissionWrapper/PermissionWrapper'
import {
  ContextMenuContainer,
  MenuItem,
} from '../../image_viewer/components/ViewerPane/components/ContextMenu/styles'

export const CardBase = styled.div`
  width: 350px;
  height: 280px;
  cursor: pointer;
  margin: 18px;
  background: #ffffff;
  box-shadow: 0 1px 20px rgba(0, 0, 0, 0.1);
  box-sizing: border-box;

  @media ${media.belowTablet} {
    width: 100%;
    margin: 0;
  }
`

export const CardImage = styled.img`
  width: 350px;
  height: 172px;

  @media ${media.belowTablet} {
    width: 100%;
    object-fit: cover;
  }
`

const CardImageWrap = styled.div`
  position: relative;
  width: 350px;
  height: 172px;

  @media ${media.belowTablet} {
    width: 100%;
    height: auto;
  }
`

const ReviewBadge = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  border: none;
  cursor: pointer;
  background: #ef7e46;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  padding: 6px 8px;
  border-radius: 4px;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.2);
`

export const CardTitle = styled.div`
  margin-top: 20px;
  margin-left: 20px;
  margin-right: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

export const CardTitleInner = styled.div`
  font-size: 20px;
  line-height: 28px;
  color: #05051d;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`

export const CardDesc = styled.div`
  margin-top: 18px;
  margin-left: 20px;
  margin-right: 20px;
  display: flex;
  justify-content: space-between;
  color: #7e858e;
`

export const CardDescItem = styled.div`
  font-size: 14px;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
`

const CardWrapper = styled.div`
  @media ${media.belowTablet} {
    display: block;
    width: 100%;
    margin: 0 0 16px;
  }
`

const CardLink = styled(Link)`
  display: contents;
  text-decoration: none;
  color: inherit;

  @media ${media.belowTablet} {
    display: block;
    width: 100%;
    margin: 0 0 16px;
  }
`

const CardStatus = styled.div<{ status: string }>`
  background-color: ${props =>
    props.status === 'hold'
      ? '#f7cd6540'
      : props.status === 'done'
        ? '#34d39940'
        : '#073c7a40'};
  color: ${props =>
    props.status === 'hold'
      ? '#F7CD65'
      : props.status === 'done'
        ? '#34D399'
        : '#073C7A'};
  font-size: 14px;
  padding: 5px 7px;
  border-radius: 5px;
`

/**
 * Temporary component to wrap cards. Allows for only specified project to be clicked.
 *
 * @param projectId
 * @param children
 */

const dummyProjects = ['EV7XcemsKA', 'Abl8diemnC', 'VAmecLab4e', 'brt79Zwxyy']
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper props type
const BetaProofLink = ({ projectId, children }: any) => {
  const { adminPrefix } = useAdminZoneContext()

  if (!dummyProjects.includes(projectId)) {
    return (
      <CardLink to={`${adminPrefix}/project/${projectId}`}>{children}</CardLink>
    )
  }
  return <CardWrapper>{children}</CardWrapper>
}

export const Card = (props: {
  public_id: string
  thumbnail_url: string
  name: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper last_modified_on type
  last_modified_on?: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper location type
  location?: any
  image_id?: string
  image_url?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper latest_data_received_on type
  latest_data_received_on?: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper status type
  status?: any
  display_progress?: boolean
  pending_review_task_count?: number
  userHasMenuAccess: boolean
  onClickMenuItem: (integration: Integrations) => void
}) => {
  const [menuOpen, setMenuOpen] = useState<boolean>(false)
  const history = useHistory()
  const { state: userState } = useUserContext()
  const pendingCount = props.pending_review_task_count || 0
  const showReviewBadge =
    pendingCount > 0 &&
    userState.user_type !== 'super' &&
    checkUserPermissions(
      userState.permissions,
      'progress_tables.access_progress_review',
      props.public_id,
    )

  const onClickMenu = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setMenuOpen(true)
  }

  const onClickReviewBadge = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const path =
      userState.user_type === 'super'
        ? `/admin/project/${props.public_id}/review`
        : `/project/${props.public_id}/review`
    history.push(path)
  }

  return (
    <BetaProofLink projectId={props.public_id}>
      <CardBase>
        <CardImageWrap>
          <CardImage src={props.image_url} />
          {showReviewBadge && (
            <ReviewBadge type="button" onClick={onClickReviewBadge}>
              Review {pendingCount}
            </ReviewBadge>
          )}
        </CardImageWrap>
        <CardTitle
          style={{
            position: 'relative',
          }}
        >
          <CardTitleInner>{props.name}</CardTitleInner>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <CardStatus status={props.status}>
              {capitalizeString(props.status)}
            </CardStatus>
            {props.userHasMenuAccess && (
              <Icon
                icon={iconVerticalDotMenu}
                size={20}
                onClick={onClickMenu}
                style={{
                  marginRight: '-8px',
                }}
              />
            )}
          </div>
          {props.userHasMenuAccess && menuOpen && (
            <IntegrationsMenu
              onCloseMenu={() => setMenuOpen(false)}
              onClickMenuItem={props.onClickMenuItem}
            />
          )}
        </CardTitle>
        <CardDesc>
          <CardDescItem>
            <Icon
              icon={iconMapMarker}
              size={16}
              style={{ marginRight: '7px' }}
            />
            <div>{props.location.city + ', ' + props.location.state_code}</div>
          </CardDescItem>
          <CardDescItem>
            Last Update:&nbsp;
            <Moment date={props.latest_data_received_on} format="MM/DD/YY" />
          </CardDescItem>
        </CardDesc>
      </CardBase>
    </BetaProofLink>
  )
}

export enum Integrations {
  Procore,
}

interface IIntegrationsMenuProps {
  onCloseMenu: () => void
  onClickMenuItem: (integration: Integrations) => void
}

const IntegrationsMenu = ({
  onCloseMenu,
  onClickMenuItem,
}: IIntegrationsMenuProps) => {
  const menuRef = createRef<HTMLDivElement>()

  useOnClickOutside(menuRef, onCloseMenu)

  const handleMenuItemClick = (
    e: React.MouseEvent<HTMLLIElement>,
    integration: Integrations,
  ) => {
    e.preventDefault()
    e.stopPropagation()

    onClickMenuItem(integration)
    onCloseMenu()
  }

  return (
    <ContextMenuContainer
      ref={menuRef}
      width={230}
      style={{
        position: 'absolute',
        left: '320px',
        background: 'rgba(255, 255, 255)',
      }}
    >
      <ul>
        <MenuItem
          onClick={e => handleMenuItemClick(e, Integrations.Procore)}
          icon={integrationMenuGreyIcon}
          hoverIcon={integrationMenuWhiteIcon}
          style={{
            padding: '5px 10px',
          }}
        >
          {integrationBranding.procore.buildingsCardLinkProject}
        </MenuItem>
      </ul>
    </ContextMenuContainer>
  )
}
